import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DateUtils } from '../../utils';
import { formatPctChange, hasPreviousMsrp, shouldShowPercentChange } from './price-sheet-utils';
import { bomGroupLabel, fmtRange, groupBOMRecords } from './bom-utils';
import { BOMRecord, ProductPriceItem } from './types';

const fmtUsd = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

/** Format size to 2 decimal places, trimming unnecessary trailing zeros (e.g. 20.00 → "20", 20.50 → "20.5"). */
export function fmtSize(n: number): string {
  return parseFloat(n.toFixed(2)).toString();
}

function dateStamp(): string {
  return new Date().toISOString().split('T')[0];
}

function safeManufacturerSlug(name: string): string {
  return name.replace(/\s+/g, '-').toLowerCase();
}

// ─── CSV helpers ─────────────────────────────────────────────────────────────

function csvCell(value: string | number): string {
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function csvRow(values: (string | number)[]): string {
  return values.map(csvCell).join(',');
}

function triggerCsvDownload(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── PDF helpers ─────────────────────────────────────────────────────────────

function addPdfHeader(
  doc: jsPDF,
  title: string,
  manufacturerName: string,
  generatedAt: string
): void {
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, 16);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Manufacturer: ${manufacturerName}`, 14, 24);
  doc.text(`Generated: ${DateUtils.parseDate(generatedAt).toFormat('MMM d, yyyy')}`, 14, 30);
}

// ─── Price Sheet (customer-facing) ───────────────────────────────────────────

export function deriveProductLabel(item: ProductPriceItem): string {
  const parts: string[] = [];
  if (item.averageSize) parts.push(`${fmtSize(item.averageSize)}'`);
  // Headers are unclassified — never surface classification on the label (UDOMI-1026)
  if (item.productType === 'Header') {
    parts.push('Header');
    return parts.join(' ');
  }
  if (item.classification) parts.push(item.classification);
  if (item.productType && item.productType !== item.classification) parts.push(item.productType);
  return parts.join(' ');
}

/** Margin factor for T1 pricing tier (default rate). */
const MARGIN_FACTOR_T1 = 0.75;

/** Margin factor for T2/T3 pricing tiers (elevated rate). */
const MARGIN_FACTOR_T2_T3 = 0.85;

/**
 * Infers the margin factor for a product based on its pricing tier category.
 * T1 products use a 0.75 margin factor; T2 and T3 products use 0.85.
 * Fallback only: the API returns the configured margin factor in dealerPricing.
 */
export function inferMarginFactor(category: string): number {
  return category.includes('T2') || category.includes('T3')
    ? MARGIN_FACTOR_T2_T3
    : MARGIN_FACTOR_T1;
}

/** Dealer Cost/Unit (MSRP × margin factor) from the API, falling back to the inferred tier factor. */
export function productCostPerUnit(item: ProductPriceItem): number {
  return (
    item.dealerPricing?.dealerCostPerUnit ?? item.pricing.msrp * inferMarginFactor(item.category)
  );
}

/** Dealer Cost/Ft (Cost/Unit ÷ size). */
export function productCostPerFoot(item: ProductPriceItem): number {
  const size = parseFloat(item.averageSize.toFixed(2));
  return size > 0 ? productCostPerUnit(item) / size : 0;
}

export function exportPriceSheetCsv(
  items: ProductPriceItem[],
  manufacturerName: string,
  generatedAt: string
): void {
  const lines: string[] = [
    csvRow(['Product Price Sheet']),
    csvRow([`Manufacturer: ${manufacturerName}`]),
    csvRow([`Generated: ${DateUtils.toDisplayDate(generatedAt)}`]),
    '',
    csvRow([
      'Code',
      'Category',
      'Risk Level',
      'Name',
      'Size (ft)',
      'Retail Price/Unit',
      'Cost/Unit',
      'Cost/Ft',
      'Old MSRP',
      'New MSRP',
      '% Change',
    ]),
  ];

  for (const item of items) {
    const costPerUnit = productCostPerUnit(item);
    const costPerFt = productCostPerFoot(item);
    const previousMsrp = item.priceHistory?.previousMsrp;
    const oldMsrp =
      hasPreviousMsrp(item.priceHistory) && previousMsrp != null ? fmtUsd(previousMsrp) : 'N/A';
    const pctChange =
      shouldShowPercentChange(item.priceHistory) && item.priceHistory?.msrpChangePercentage != null
        ? formatPctChange(item.priceHistory.msrpChangePercentage)
        : '';

    lines.push(
      csvRow([
        item.code ?? '',
        item.category,
        item.riskLevel,
        deriveProductLabel(item),
        fmtSize(item.averageSize),
        fmtUsd(item.pricing.msrp),
        fmtUsd(costPerUnit),
        fmtUsd(costPerFt),
        oldMsrp,
        fmtUsd(item.pricing.msrp),
        pctChange,
      ])
    );
  }

  triggerCsvDownload(
    lines.join('\n'),
    `price-sheet-${safeManufacturerSlug(manufacturerName)}-${dateStamp()}.csv`
  );
}

export function exportPriceSheetPdf(
  items: ProductPriceItem[],
  manufacturerName: string,
  generatedAt: string,
  riskLabel: string
): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  addPdfHeader(doc, 'Product Price Sheet', manufacturerName, generatedAt);

  // Group by category for section headers
  const grouped = new Map<string, ProductPriceItem[]>();
  items.forEach(item => {
    if (!grouped.has(item.category)) grouped.set(item.category, []);
    grouped.get(item.category)!.push(item);
  });

  let startY = 38;

  grouped.forEach((categoryItems, category) => {
    // Category label
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(25, 118, 210);
    doc.rect(14, startY, doc.internal.pageSize.getWidth() - 28, 6, 'F');
    doc.text(`${category}  (${riskLabel})`, 16, startY + 4.2);
    doc.setTextColor(0, 0, 0);

    const tableRows = categoryItems.map(item => {
      const costPerUnit = productCostPerUnit(item);
      const costPerFt = productCostPerFoot(item);
      return [
        item.code ?? '',
        deriveProductLabel(item),
        `${fmtSize(item.averageSize)}'`,
        fmtUsd(item.pricing.msrp),
        fmtUsd(costPerUnit),
        fmtUsd(costPerFt),
        hasPreviousMsrp(item.priceHistory) && item.priceHistory?.previousMsrp != null
          ? fmtUsd(item.priceHistory.previousMsrp)
          : 'N/A',
        fmtUsd(item.pricing.msrp),
        shouldShowPercentChange(item.priceHistory) &&
        item.priceHistory?.msrpChangePercentage != null
          ? formatPctChange(item.priceHistory.msrpChangePercentage)
          : '',
      ];
    });

    autoTable(doc, {
      startY: startY + 7,
      head: [
        [
          'Code',
          'Name',
          'Size',
          'Retail Price/Unit',
          'Cost/Unit',
          'Cost/Ft',
          'Old MSRP',
          'New MSRP',
          '% Change',
        ],
      ],
      body: tableRows,
      theme: 'striped',
      headStyles: { fillColor: [66, 66, 66], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 45 },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' },
        7: { halign: 'right' },
        8: { halign: 'right' },
      },
      margin: { left: 14, right: 14 },
    });

    startY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
  });

  doc.save(`price-sheet-${safeManufacturerSlug(manufacturerName)}-${dateStamp()}.pdf`);
}

// ─── Price Totals (internal cost breakdown) ───────────────────────────────────

export function exportPriceTotalsCsv(
  items: ProductPriceItem[],
  manufacturerName: string,
  generatedAt: string
): void {
  const lines: string[] = [
    csvRow(['Product Size Price Totals']),
    csvRow([`Manufacturer: ${manufacturerName}`]),
    csvRow([`Generated: ${DateUtils.toDisplayDate(generatedAt)}`]),
    '',
    csvRow([
      'Code',
      'Category',
      'Risk Level',
      'Product',
      'Size (ft)',
      'Cost of Steel',
      'Cost of Paint',
      'Hardware Cost',
      'Labor Cost',
      'Total Cost',
      'MSRP',
      'Revenue',
      'Cost/Ft',
    ]),
  ];

  for (const item of items) {
    lines.push(
      csvRow([
        item.code ?? '',
        item.category,
        item.riskLevel,
        deriveProductLabel(item),
        fmtSize(item.averageSize),
        fmtUsd(item.costs.steelCost),
        fmtUsd(item.costs.paintCost),
        fmtUsd(item.costs.hardwareCost),
        fmtUsd(item.costs.laborCost),
        fmtUsd(item.costs.totalCost),
        fmtUsd(item.pricing.msrp),
        fmtUsd(item.pricing.revenue),
        fmtUsd(item.pricing.costPerFoot),
      ])
    );
  }

  triggerCsvDownload(
    lines.join('\n'),
    `price-totals-${safeManufacturerSlug(manufacturerName)}-${dateStamp()}.csv`
  );
}

export function exportPriceTotalsPdf(
  items: ProductPriceItem[],
  manufacturerName: string,
  generatedAt: string,
  riskLabel: string
): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  addPdfHeader(doc, 'Product Size Price Totals', manufacturerName, generatedAt);

  const grouped = new Map<string, ProductPriceItem[]>();
  items.forEach(item => {
    if (!grouped.has(item.category)) grouped.set(item.category, []);
    grouped.get(item.category)!.push(item);
  });

  let startY = 38;

  grouped.forEach((categoryItems, category) => {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(25, 118, 210);
    doc.rect(14, startY, doc.internal.pageSize.getWidth() - 28, 6, 'F');
    doc.text(`${category}  (${riskLabel})`, 16, startY + 4.2);
    doc.setTextColor(0, 0, 0);

    const tableRows = categoryItems.map(item => [
      item.code ?? '',
      deriveProductLabel(item),
      `${fmtSize(item.averageSize)}'`,
      fmtUsd(item.costs.steelCost),
      fmtUsd(item.costs.paintCost),
      fmtUsd(item.costs.hardwareCost),
      fmtUsd(item.costs.laborCost),
      fmtUsd(item.costs.totalCost),
      fmtUsd(item.pricing.msrp),
      fmtUsd(item.pricing.revenue),
      fmtUsd(item.pricing.costPerFoot),
    ]);

    autoTable(doc, {
      startY: startY + 7,
      head: [
        [
          'Code',
          'Product',
          'Size',
          'Steel Cost',
          'Paint Cost',
          'Hardware',
          'Labor',
          'Total Cost',
          'MSRP',
          'Revenue',
          'Cost/Ft',
        ],
      ],
      body: tableRows,
      theme: 'striped',
      headStyles: { fillColor: [66, 66, 66], fontSize: 7.5 },
      bodyStyles: { fontSize: 7.5 },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 38 },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' },
        7: { halign: 'right' },
        8: { halign: 'right' },
        9: { halign: 'right' },
        10: { halign: 'right' },
      },
      margin: { left: 14, right: 14 },
    });

    startY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
  });

  doc.save(`price-totals-${safeManufacturerSlug(manufacturerName)}-${dateStamp()}.pdf`);
}

// ─── BOM Specifications (read-only) ──────────────────────────────────────────

const bomRow = (record: BOMRecord): (string | number)[] => [
  record.trussCode,
  bomGroupLabel(record),
  record.riskLevel,
  `${record.windSpeedFrom}-${record.windSpeedTo}`,
  fmtRange(record.spanFrom, record.spanTo),
  record.costs.isPriced ? fmtUsd(record.costs.steelCost) : 'Missing Price',
  record.costs.isPriced ? fmtUsd(record.costs.hardwareCost) : 'Missing Price',
  fmtUsd(record.costs.paintCost),
  record.costs.laborHours.toFixed(1),
  fmtUsd(record.costs.laborCost),
  record.costs.isPriced ? fmtUsd(record.costs.totalCost) : 'Missing Price',
  record.msrp != null ? fmtUsd(record.msrp) : 'N/A',
];

export function exportBOMSpecificationsCsv(
  records: BOMRecord[],
  manufacturerName: string,
  generatedAt: string
): void {
  const lines: string[] = [
    csvRow(['BOM Specifications']),
    csvRow([`Manufacturer: ${manufacturerName}`]),
    csvRow([`Generated: ${DateUtils.toDisplayDate(generatedAt)}`]),
    '',
    csvRow([
      'Code',
      'Group',
      'Risk Level',
      'Wind (mph)',
      'Span (ft)',
      'Cost of Steel',
      'Hardware Cost',
      'Cost of Paint',
      'Labor Hrs',
      'Labor Cost',
      'Total Cost',
      'MSRP',
    ]),
    ...records.map(record => csvRow(bomRow(record))),
  ];

  triggerCsvDownload(
    lines.join('\n'),
    `bom-specifications-${safeManufacturerSlug(manufacturerName)}-${dateStamp()}.csv`
  );
}

export function exportBOMSpecificationsPdf(
  records: BOMRecord[],
  manufacturerName: string,
  generatedAt: string,
  riskLabel: string
): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  addPdfHeader(doc, 'BOM Specifications', manufacturerName, generatedAt);

  let startY = 38;

  groupBOMRecords(records).forEach(group => {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(25, 118, 210);
    doc.rect(14, startY, doc.internal.pageSize.getWidth() - 28, 6, 'F');
    doc.text(`${group.label}  (${riskLabel})`, 16, startY + 4.2);
    doc.setTextColor(0, 0, 0);

    autoTable(doc, {
      startY: startY + 7,
      head: [
        [
          'Code',
          'Span (ft)',
          'Steel',
          'Hardware',
          'Paint',
          'Labor Hrs',
          'Labor',
          'Total Cost',
          'MSRP',
        ],
      ],
      body: group.records.map(record => {
        const row = bomRow(record);
        return [row[0], row[4], row[5], row[6], row[7], row[8], row[9], row[10], row[11]];
      }),
      theme: 'striped',
      headStyles: { fillColor: [66, 66, 66], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      columnStyles: {
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' },
        7: { halign: 'right' },
        8: { halign: 'right' },
      },
      margin: { left: 14, right: 14 },
    });

    startY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
  });

  doc.save(`bom-specifications-${safeManufacturerSlug(manufacturerName)}-${dateStamp()}.pdf`);
}
